import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlerService } from './error-handler/error-handler.service';
import { LocalStorageService } from './local-storage.service';
import { NotificationService } from './notification/notification.service';
import { LOCALSTORAGE_KEYS } from './constants/databases';
import { SESSION_ENDED_REASONS } from './constants/errors';
import { Session } from './types/session.type';

/**
 * A tab opened inside the click and still waiting for its content. `filled` tells a late
 * error path whether anything ever reached it, so a tab left on the placeholder gets closed
 * instead of sitting there forever.
 */
export interface PendingTab {
    win: Window | null;
    filled: boolean;
}

const GENERIC_ERROR = 'We could not open this invoice. Please try again.';
/** The invoice does not exist, or it is not this customer's — the backend answers 404 to both so
 *  it never confirms which. The copy stays just as vague, and drops the "try again" that would be
 *  wrong advice either way. */
const NOT_FOUND_ERROR = 'We could not find this invoice. Please contact support if this keeps happening.';
/** A plain string on purpose: `ERROR_REASONS` does not exist on every brand (see errors.ts). */
const INVOICE_NOT_FOUND = 'INVOICE_NOT_FOUND';
const BLOCKED_ERROR = 'Your browser blocked the invoice window. Allow pop-ups for this site and try again.';
/** Long enough for the tab to have loaded the PDF, short enough not to hold the bytes around. */
const REVOKE_AFTER_MS = 60_000;

/**
 * Fetches binary bodies the browser cannot request on its own, and puts them in a tab.
 *
 * Deliberately separate from `ApiService` rather than an option on it: a non-JSON response
 * needs `responseType: 'blob'`, and with that set Angular hands the ERROR body back as a Blob
 * too. `ErrorHandlerService` reads `error.error.reason` to tell a dead session from a refused
 * action, and against a Blob that read is always `undefined` — which `errors.ts` then resolves
 * as "a bare 403 means the session ended" and signs the customer out. So the error body is read
 * back to JSON here, and the shared handler only sees errors in the shape it expects.
 *
 * DEV-11537.
 */
@Injectable({ providedIn: 'root' })
export class FileRequestService {

    private http = inject(HttpClient);
    private localStorageService = inject(LocalStorageService);
    private errorHandlerService = inject(ErrorHandlerService);
    private notificationService = inject(NotificationService);
    private platformId = inject(PLATFORM_ID);

    /**
     * Opens a blank tab and parks a placeholder in it. MUST be called synchronously from the
     * click handler: Safari only lets a tab through while the user gesture is live (about a
     * second), and fetching an invoice takes longer than that. Measured on iOS 18.7 — a
     * `window.open` after the await is met with a pop-up permission prompt three seconds after
     * the tap, and a customer who dismisses it never sees the file.
     */
    openPendingTab(): PendingTab {
        if (!isPlatformBrowser(this.platformId)) return { win: null, filled: false };

        const win = window.open('', '_blank');
        win?.document.write(
            '<title>Invoice</title>' +
            '<p style="font:16px -apple-system,system-ui,sans-serif;padding:24px;color:#444">Loading your invoice…</p>'
        );
        win?.document.close();

        return { win, filled: false };
    }

    /**
     * Sends `invoiceURL` to the waiting tab, handling both shapes of the contract.
     *
     * Until the backend has shipped to every brand, the field arrives two ways: the absolute,
     * publicly readable URL it has always been, and the relative API route (DEV-11537) that
     * only answers with a Bearer header. Detect it rather than assume it — that is what lets
     * the front deploy ahead of the backend, brand by brand, with nothing broken in between.
     */
    showInTab(pending: PendingTab, invoiceURL?: string | null): Observable<void> {
        if (!invoiceURL) {
            this.fail(pending, GENERIC_ERROR);
            return of(void 0);
        }

        if (!invoiceURL.startsWith('/api/')) {
            this.assign(pending, invoiceURL);
            return of(void 0);
        }

        return this.getBlob(invoiceURL).pipe(
            tap(blob => {
                // `route` has already said whatever needed saying — and when it decided the
                // session is gone, a toast here would flash just before the reload.
                if (!blob) return this.discard(pending);
                const objectUrl = URL.createObjectURL(blob);
                this.assign(pending, objectUrl);
                setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_AFTER_MS);
            }),
            map(() => void 0)
        );
    }

    /**
     * Closes a tab nothing ever reached. `ApiService.handleRequest` turns a failed request into
     * `of(null)` and then filters it out, so an earlier step can complete without ever emitting
     * — leaving this tab on the placeholder with no error path of its own.
     */
    closeIfPending(pending: PendingTab): void {
        if (pending.filled) return;
        this.fail(pending);
    }

    private getBlob(path: string): Observable<Blob | null> {
        const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
        const token = session?.accountInfo?.token;

        // `path` already carries `/api/v3`, so it hangs off the bare origin — `apis.v3` would
        // repeat the prefix and 404. The trailing slash has to go: `wh` and `pb` keep one in
        // `base_api`, and `//api/v3/...` either 404s or redirects, and a redirect is no place
        // to be carrying an Authorization header.
        const origin = environment.apis.v1.replace(/\/+$/, '');

        return this.http.get(`${origin}${path}`, {
            headers: new HttpHeaders({ ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }),
            responseType: 'blob'
        }).pipe(
            catchError((error: HttpErrorResponse) => from(this.readError(error)).pipe(
                switchMap(normalized => this.route(normalized)),
                map(() => null)
            )),
            take(1)
        );
    }

    /**
     * Reads the error body back out of its Blob. Always resolves to an object: the shared
     * handler reaches for `error.error.data` without guarding, so a 502 carrying HTML or a
     * network failure carrying nothing would throw on the way out.
     */
    private async readError(error: HttpErrorResponse): Promise<{ status: number; error: any }> {
        let body: any = {};

        if (error.error instanceof Blob) {
            try { body = JSON.parse(await error.error.text()); } catch (e) { body = {}; }
        } else if (error.error) {
            body = error.error;
        }

        return { status: error.status ?? 0, error: body };
    }

    private route(error: { status: number; error: any }): Observable<null> {
        const reason = error.error?.reason;

        // Only an explicit session reason may sign the customer out. This endpoint answers 403
        // for a dead token but 404 for someone else's invoice, and `isSessionEndedResponse`
        // treats any unexplained 403 as a dead session — so a 403 whose body we could not read
        // must not take the customer's session with it.
        if (reason && SESSION_ENDED_REASONS.includes(reason))
            return this.errorHandlerService.handleHttpError(error);

        // Every message this endpoint returns is written for a developer ("Not found", "User
        // does not exist", "unexpected error: <exception>"), so none of them is shown as-is —
        // the last one would put a Python traceback in front of a customer. The reason is what
        // we read instead.
        const text = reason === INVOICE_NOT_FOUND ? NOT_FOUND_ERROR : GENERIC_ERROR;
        this.notificationService.show({ text, type: 'error' });
        return of(null);
    }

    private assign(pending: PendingTab, url: string): void {
        pending.filled = true;

        if (pending.win) {
            pending.win.location.href = url;
            return;
        }

        // The placeholder tab never opened. Worth one more try — when the URL was already known
        // at click time this still runs inside the gesture.
        if (!window.open(url, '_blank'))
            this.notificationService.show({ text: BLOCKED_ERROR, type: 'error' });
    }

    private fail(pending: PendingTab, text: string = GENERIC_ERROR): void {
        this.discard(pending);
        this.notificationService.show({ text, type: 'error' });
    }

    private discard(pending: PendingTab): void {
        pending.filled = true;
        pending.win?.close();
    }

}
